/**
 * =============================================================================
 * Main Layout Component
 * =============================================================================
 *
 * Application layout with sidebar navigation and header.
 *
 * Structure:
 * - Sidebar: Logo, navigation menu, user info
 * - Header: Page title, user menu
 * - Main: Page content (Outlet)
 */

import { Outlet, NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { APP_VERSION } from '../utils/version'
import { sat } from '../utils/serviceAccountsI18n'

// =============================================================================
// Navigation Items
// =============================================================================

interface NavItem {
  name: string
  path: string
  icon: string
  roles?: string[] // Required roles (empty = all authenticated users)
}

/**
 * Navigation menu items.
 * Each item can optionally require specific roles.
 */
const navItems: NavItem[] = [
  {
    name: 'Dashboard',
    path: '/',
    icon: '📊',
  },
  {
    name: 'Institutions',
    path: '/institutions',
    icon: '🏥',
    roles: ['system_admin', 'rehapiano_admin'],
  },
  {
    name: 'Devices',
    path: '/devices',
    icon: '🎹',
    roles: ['system_admin', 'rehapiano_admin', 'institution_admin'],
  },
  {
    name: 'Hands',
    path: '/hands',
    icon: '✋',
    roles: ['system_admin', 'rehapiano_admin', 'institution_admin'],
  },
  {
    name: 'Local Users',
    path: '/local-users',
    icon: '🔐',
    roles: ['system_admin', 'rehapiano_admin'],
  },
  {
    name: sat('navTitle'),
    path: '/service-accounts',
    icon: '🤖',
    roles: ['system_admin'],
  },
  {
    name: 'Používatelia inštitúcií',
    path: '/institution-users',
    icon: '🩺',
    roles: ['system_admin', 'rehapiano_admin', 'institution_admin'],
  },
  {
    name: 'App Teams',
    path: '/app-teams',
    icon: '👥',
    roles: ['system_admin', 'rehapiano_admin', 'app_admin', 'app_dev'],
  },
  {
    name: 'Apps',
    path: '/apps',
    icon: '📱',
    roles: ['system_admin', 'rehapiano_admin', 'app_admin', 'app_dev'],
  },
  {
    name: 'Device Types',
    path: '/device-types',
    icon: '🧩',
    roles: ['system_admin', 'rehapiano_admin', 'app_admin'],
  },
  {
    name: 'Sync Health',
    path: '/records',
    icon: '🗄️',
    roles: ['system_admin', 'rehapiano_admin', 'institution_admin'],
  },
]

// =============================================================================
// Layout Component
// =============================================================================

/**
 * Main layout component.
 * Renders sidebar, header, and main content area.
 */
const Layout: React.FC = () => {
  const { user, logout, hasRole } = useAuth()
  const location = useLocation()

  // Debug logging
  console.log('[Layout] User roles:', user?.roles)
  console.log('[Layout] hasRole rehapiano_admin:', hasRole('rehapiano_admin'))
  console.log('[Layout] hasRole institution_admin:', hasRole('institution_admin'))
  console.log('[Layout] hasRole app_admin:', hasRole('app_admin'))

  /**
   * Get current page title from navigation items.
   */
  const getPageTitle = (): string => {
    const currentItem = navItems.find(item => item.path === location.pathname)
    return currentItem?.name || 'Admin Portal'
  }

  /**
   * Check if user can access a navigation item.
   */
  const canAccess = (item: NavItem): boolean => {
    if (!item.roles || item.roles.length === 0) {
      return true
    }
    const hasAccess = item.roles.some(role => hasRole(role))
    console.log(`[Layout] canAccess ${item.name}:`, hasAccess, 'required:', item.roles)
    return hasAccess
  }

  return (
    <div className="min-h-screen flex">
      {/* =================================================================
          Sidebar
          ================================================================= */}
      <aside className="w-64 bg-gray-900 text-white flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-gray-700">
          <h1 className="text-xl font-bold">RehaPiano</h1>
          <p className="text-sm text-gray-400">Admin Portal</p>
          <p className="text-xs text-gray-600">v{APP_VERSION}</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4">
          <ul className="space-y-1">
            {(() => {
              const filteredItems = navItems.filter(canAccess)
              console.log('[Layout] Filtered nav items:', filteredItems.map(i => i.name))
              return filteredItems
            })().map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-6 py-3 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`
                  }
                >
                  <span className="text-lg">{item.icon}</span>
                  {item.name}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-gray-700">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center">
              <span className="text-sm font-medium">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-gray-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full px-4 py-2 text-sm text-gray-300 hover:text-white
                       hover:bg-gray-800 rounded-lg transition-colors"
          >
            Sign Out
          </button>
        </div>
      </aside>

      {/* =================================================================
          Main Content
          ================================================================= */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">{getPageTitle()}</h2>

            {/* Role Badges - show all user roles */}
            <div className="flex items-center gap-2 flex-wrap">
              {user?.roles.includes('system_admin') && (
                <span className="badge badge-active">System Admin</span>
              )}
              {user?.roles.includes('rehapiano_admin') && (
                <span className="badge bg-emerald-100 text-emerald-800">RehaPiano Admin</span>
              )}
              {user?.roles.includes('institution_admin') && (
                <span className="badge bg-blue-100 text-blue-800">Institution Admin</span>
              )}
              {user?.roles.includes('app_admin') && (
                <span className="badge bg-orange-100 text-orange-800">App Admin</span>
              )}
              {user?.roles.includes('app_dev') && (
                <span className="badge bg-purple-100 text-purple-800">App Developer</span>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 bg-gray-50 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default Layout
